import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, CheckCircle2, AlertTriangle, Receipt } from "lucide-react";
import { api, errMsg, inr, fmtDate } from "../lib/api";
import { PageHeader, Card, StatCard } from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sel, Field } from "../components/Sel";
import { PayBadge } from "../components/Badges";

const PaymentDialog = ({ deal, onClose, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => {
    if (deal) setF({ deal_amount: deal.deal_amount ?? deal.value, paid_amount: deal.paid_amount || 0, invoice_no: deal.invoice_no || "", invoice_date: deal.invoice_date || "", payment_status: deal.payment_status || "pending", payment_notes: deal.payment_notes || "" });
  }, [deal]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    try {
      await api.put(`/accounts/deals/${deal.id}`, { ...f, deal_amount: Number(f.deal_amount) || 0, paid_amount: Number(f.paid_amount) || 0 });
      toast.success("Payment updated");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!deal} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="payment-dialog">
        <DialogHeader><DialogTitle>Payment & invoice · {deal?.company || deal?.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Deal amount"><Input data-testid="pay-deal-amount" type="number" value={f.deal_amount ?? ""} onChange={set("deal_amount")} /></Field>
          <Field label="Paid amount"><Input data-testid="pay-paid-amount" type="number" value={f.paid_amount ?? ""} onChange={set("paid_amount")} /></Field>
          <Field label="Invoice no."><Input data-testid="pay-invoice-no" value={f.invoice_no || ""} onChange={set("invoice_no")} /></Field>
          <Field label="Invoice date"><Input data-testid="pay-invoice-date" type="date" value={f.invoice_date || ""} onChange={set("invoice_date")} /></Field>
          <div className="col-span-2"><Field label="Payment status"><Sel testid="pay-status-select" value={f.payment_status} onChange={set("payment_status")} options={["pending", "partial", "paid", "overdue"]} /></Field></div>
          <div className="col-span-2"><Field label="Notes"><Textarea data-testid="pay-notes" rows={2} value={f.payment_notes || ""} onChange={set("payment_notes")} /></Field></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} data-testid="pay-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Accounts() {
  const [deals, setDeals] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/accounts/deals").then((r) => setDeals(r.data));
  useEffect(() => { load(); }, []);
  const total = deals.reduce((a, d) => a + (d.deal_amount ?? d.value ?? 0), 0);
  const paid = deals.reduce((a, d) => a + (d.paid_amount || 0), 0);

  return (
    <div data-testid="accounts-page">
      <PageHeader title="Accounts" subtitle="Won deals, invoices and payment collection." />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatCard label="Won deals" value={deals.length} icon={Receipt} testid="acc-won-deals" />
        <StatCard label="Total billed" value={inr(total)} icon={Wallet} tone="blue" testid="acc-total-billed" />
        <StatCard label="Collected" value={inr(paid)} icon={CheckCircle2} testid="acc-collected" />
        <StatCard label="Outstanding" value={inr(total - paid)} icon={AlertTriangle} tone="rose" testid="acc-outstanding" />
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="deals-table">
          <thead><tr className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
            {["Client", "Invoice", "Deal amount", "Paid", "Balance", "Status", "Won on", ""].map((h) => <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {deals.map((d) => {
              const amt = d.deal_amount ?? d.value ?? 0;
              return (
                <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50/80" data-testid={`deal-row-${d.id}`}>
                  <td className="px-4 py-3"><div className="font-semibold">{d.company || d.name}</div><div className="text-xs text-slate-500">{d.name} · {d.assigned_name}</div></td>
                  <td className="px-4 py-3 font-mono text-xs">{d.invoice_no || "-"}</td>
                  <td className="px-4 py-3 font-mono font-semibold whitespace-nowrap">{inr(amt)}</td>
                  <td className="px-4 py-3 font-mono text-emerald-700 whitespace-nowrap">{inr(d.paid_amount)}</td>
                  <td className="px-4 py-3 font-mono text-rose-600 whitespace-nowrap">{inr(amt - (d.paid_amount || 0))}</td>
                  <td className="px-4 py-3"><PayBadge s={d.payment_status} testid={`deal-status-${d.id}`} /></td>
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(d.won_at)}</td>
                  <td className="px-4 py-3"><Button size="sm" variant="outline" onClick={() => setSel(d)} data-testid={`update-payment-${d.id}`}>Update</Button></td>
                </tr>
              );
            })}
            {!deals.length && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">No won deals yet.</td></tr>}
          </tbody>
        </table>
      </Card>
      <PaymentDialog deal={sel} onClose={() => setSel(null)} onSaved={load} />
    </div>
  );
}
